import { keepPreviousData, queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { api } from '@/api';
import { DateRangeParams } from '@/api/apis/resources/schedule';
import { scheduleQueryKeys } from '@/queries/resource/schedule/schedule.keys';

export const csScheduleQueries = {
  shifts: (dateRange: DateRangeParams, processId?: string | null) => {
    return queryOptions({
      queryKey: scheduleQueryKeys.cs.getShiftsKey(dateRange, processId),
      queryFn: () =>
        (processId
          ? api.schedule.cs.getCSPlanningShifts(processId, dateRange)
          : api.schedule.cs.getCSShifts(dateRange)
        ).then((data) => data.resourcesShifts),
    });
  },
  regions: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getRegionsKey(),
      queryFn: () => api.schedule.cs.getCSRegions(),
    }),
  schedule: (startDayOfMonth: Date) =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getScheduleKey(startDayOfMonth),
      queryFn: () => api.schedule.cs.getCSSchedule(startDayOfMonth),
    }),
  planningProcess: (processId?: string | null) =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getPlanningProcessKey(processId),
      queryFn: processId ? () => api.schedule.cs.getCSPlanningProcessById(processId) : skipToken,
    }),
  validation: (scheduleId?: string) =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getValidationKey(scheduleId),
      queryFn: scheduleId ? () => api.schedule.cs.getCSValidation(scheduleId) : skipToken,
    }),
  rules: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getRulesKey(),
      queryFn: () => api.schedule.cs.getCSScheduleRules(),
    }),
  inputs: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getInputsKey(),
      queryFn: () => api.schedule.cs.getCSScheduleInputs(),
    }),
  periodTimeGroupInputs: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getPeriodTimeGroupInputsKey(),
      queryFn: () => api.schedule.cs.getCSSchedulePeriodTimeGroupInputs(),
    }),
  periodRules: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getPeriodRulesKey(),
      queryFn: () => api.schedule.cs.getCSSchedulePeriodRules(),
    }),
  periodAtHubWeeklyInputs: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getPeriodAtHubWeeklyInputsKey(),
      queryFn: () => api.schedule.cs.getCSSchedulePeriodAtHubWeeklyInputs(),
    }),
  periodDeductionShiftRules: () =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getPeriodDeductionShiftRulesKey(),
      queryFn: () => api.schedule.cs.getCSSchedulePeriodDeductionShiftRules(),
    }),
  hrSystemConflicts: (scheduleId?: string) =>
    queryOptions({
      queryKey: scheduleQueryKeys.cs.getHrSystemConflictsKey(scheduleId),
      queryFn: scheduleId ? () => api.schedule.cs.getCSScheduleHrSystemConflicts(scheduleId) : skipToken,
    }),
};

export const useCSScheduleShiftsQuery = (dateRange: DateRangeParams, processId?: string | null, isPlanned = false) => {
  return useQuery({
    ...csScheduleQueries.shifts(dateRange, isPlanned ? processId : undefined),
    placeholderData: keepPreviousData,
  });
};

export const useCSScheduleRegionsQuery = () => {
  return useQuery({
    ...csScheduleQueries.regions(),
    staleTime: 30 * 60 * 1000, // 30 min
    placeholderData: keepPreviousData,
  });
};

export const useCSMonthScheduleQuery = (startDayOfMonth: Date, refetchTime?: number) => {
  return useQuery({
    ...csScheduleQueries.schedule(startDayOfMonth),
    refetchInterval: refetchTime,
  });
};

export const useCSValidationQuery = (scheduleId?: string, enabled = false) => {
  return useQuery({
    ...csScheduleQueries.validation(scheduleId),
    enabled: Boolean(scheduleId && enabled),
  });
};

export const useCSPlanningProcessQuery = (processId?: string | null, enabled = false, refetchTime?: number) => {
  return useQuery({
    ...csScheduleQueries.planningProcess(processId),
    enabled: Boolean(processId && enabled),
    refetchInterval: refetchTime,
  });
};

export const useCSScheduleSettingsRulesQuery = () => {
  return useQuery(csScheduleQueries.rules());
};

export const useCSScheduleSettingsInputsQuery = () => {
  return useQuery(csScheduleQueries.inputs());
};

export const useCSSchedulePeriodTimeGroupInputsQuery = () => {
  return useQuery(csScheduleQueries.periodTimeGroupInputs());
};

export const useCSSchedulePeriodRulesQuery = () => {
  return useQuery(csScheduleQueries.periodRules());
};

export const useCSSchedulePeriodAtHubWeeklyInputsQuery = () => {
  return useQuery(csScheduleQueries.periodAtHubWeeklyInputs());
};

export const useCSSchedulePeriodDeductionShiftRulesQuery = () => {
  return useQuery(csScheduleQueries.periodDeductionShiftRules());
};

export const useCSHrSystemConflictsQuery = (scheduleId?: string, enabled = false) => {
  return useQuery({
    ...csScheduleQueries.hrSystemConflicts(scheduleId),
    enabled: Boolean(scheduleId && enabled),
  });
};
